import Input from 'components/final-form/Input';
import TextArea from 'components/final-form/TextArea';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import React, { useRef, useState } from "react";
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Grid, Icon, List, Loader, Modal, Segment } from "semantic-ui-react";
import styled from "styled-components";
import { PatientEmailFormDto, PdfResponse, UnableToWorkEmailRequest } from "ts-types/api.types";
import { composeValidators, multipleEmailsValidator, required } from 'utils/validatorUtils';

const StyledModal = styled(Modal)`
  height: 90vh;
  width: 90vw !important;
`;

const StyledModalContent = styled(Modal.Content)`
  padding: 0 !important;
  height: 100% !important;

  .ui.inline.loader.active.send-email-in-progress {
    margin-top: -0.25rem;
    margin-left: 0.375rem;
  }
`;

const StyledSegmentGroup = styled(Segment.Group)`
  margin: 0 !important;
  height: 100% !important;
  border: none !important;
`;

const LeftSegment = styled(Segment)`
  width: 70% !important;
  padding: 0 !important;
`;

const RightSegment = styled(Segment.Group)`
  width: 30% !important;
  margin: 0 !important;
  border: none !important;
`;

const ListSegment = styled(Segment)`
  padding: 0 !important;
`;

const ButtonSegment = styled(Segment)`
  height: 8% !important;
  min-height: 50px !important;
  padding-top: 7% !important;
  padding-bottom: 6% !important;
  align-items: center;

  & button {
    float: right;
  }
`;

const ListContent = styled.div`
  width: 90%;
  display: inline-block;
  align-items: center;
`;

const DownloadSection = styled.div`
  width: 10%;
  display: inline-block;
  align-items: center;
  vertical-align: top;
`;

const StyledListItem = styled(List.Item)`
  padding: 0.75rem !important;

  .file-name {
    word-break: break-all;
  }
`;

const StyledForm = styled.form`
  padding: 0 1rem;

  label {
    font-weight: bold;
  }
`;

const btnStyle = {
  background: "transparent",
  cursor: "pointer",
  padding: "5px 10px"
};

interface FormData {
  emailsTo: string;
  emailsCc: string;
  subject: string;
  messageText: string;
}


interface Props extends MessageSourceProps {
  pdfResponses: Array<PatientEmailFormDto>;
  modalOpen: boolean;
  onCloseModal: () => void;
  getPdfUrl: (pdfResponse: PdfResponse) => string;

  showSendEmail: boolean;
  sendEmailInProgress: boolean;
  sendEmailTextKey: string;
  sendEmail: (emailRequest: UnableToWorkEmailRequest) => string;

  modalOpenFromPendingEmails: boolean;
}

const UnableToWorkPdfModal = (props: Props) => {

  const {
    pdfResponses,
    modalOpen,
    onCloseModal,
    getMessage,
    getPdfUrl,
    showSendEmail,
    sendEmailInProgress,
    sendEmailTextKey,
    sendEmail,
    modalOpenFromPendingEmails
  } = props;

  if (!pdfResponses || pdfResponses.length == 0) {
    onCloseModal();
  }

  const [selectedPdfResponse, setSelectedPdfResponse] =
      useState<PdfResponse>(pdfResponses[0]);

  const pdfUrl: string = getPdfUrl(selectedPdfResponse);

  const mappedPdfResponses = pdfResponses.map((pdfResponse, index) => (
      <StyledListItem
          key={index}
          active={selectedPdfResponse.pdfId == pdfResponse.pdfId}
          onClick={() => setSelectedPdfResponse(pdfResponse)}
      >
        <List.Icon style={{padding: "10px"}} name="file pdf outline" size="large" verticalAlign="middle"/>
        <List.Content>
          <ListContent>
            <List.Header className="file-name" as="h4">{pdfResponse.pdfName}</List.Header>
            <List.Description as="span">{pdfResponse.pdfSize}</List.Description>
          </ListContent>
          <DownloadSection>
            <Button
                as="a"
                href={getPdfUrl(pdfResponse)}
                download={pdfResponse.pdfName}
                icon
                style={btnStyle}
            >
              <Icon name="download" size="large"/>
            </Button>
          </DownloadSection>
        </List.Content>
      </StyledListItem>
  ));

  const initialPatientEmailFormValues = useRef<FormData>({
    emailsTo: pdfResponses[0] ? pdfResponses[0].emailsTo : "",
    emailsCc: pdfResponses[0] ? pdfResponses[0].emailsCc : "",
    subject: pdfResponses[0] ? pdfResponses[0].subject : "",
    messageText: pdfResponses[0] ? pdfResponses[0].messageText : ""
  });

  const handleSubmit = (values: any) => {

    const emailRequest: UnableToWorkEmailRequest = {
      pdfId: pdfResponses[0].pdfId,
      pdfName: pdfResponses[0].pdfName,
      emailsTo: values.emailsTo,
      emailsCc: values.emailsCc,
      subject: values.subject,
      messageText: values.messageText
    }

    sendEmail(emailRequest);
  }

  const renderFinalForm = () => {
    return (
        <FinalForm
            onSubmit={(values) => handleSubmit(values)}
            initialValues={initialPatientEmailFormValues.current}
            subscription={{pristine: true, submitting: true}}
            render={renderForm}
        />
    );
  }

  const renderForm = ({form, handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const validators = composeValidators(required, multipleEmailsValidator);

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column>
                    <label htmlFor="emailsTo">{getMessage("unableToWorkEmailForm.email.to")}</label>
                    <Field
                        name="emailsTo"
                        component={Input}
                        validate={validators}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <label htmlFor="emailsCc">{getMessage("unableToWorkEmailForm.email.cc")}</label>
                    <Field
                        name="emailsCc"
                        component={Input}
                        validate={validators}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <label htmlFor="subject">{getMessage("unableToWorkEmailForm.subject")}</label>
                    <Field
                        name="subject"
                        component={Input}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <label htmlFor="messageText">{getMessage("unableToWorkEmailForm.message")}</label>
                    <Field
                        name="messageText"
                        component={TextArea}
                        placeholder={`${getMessage("unableToWorkEmailForm.messageText")}...`}
                        style={{minHeight: "20vh", maxHeight: "50vh"}}
                        disabled={modalOpenFromPendingEmails}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <Button compact type="submit" primary disabled={submitting}>
                      {getMessage(sendEmailTextKey)}
                      {
                          sendEmailInProgress &&
                          <Loader className="send-email-in-progress" size="mini" active inline/>
                      }
                    </Button>
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>
          </FormContainer>
        </StyledForm>
    )
  }

  return (
      <StyledModal
          open={modalOpen}
          onClose={onCloseModal}
      >
        <StyledModalContent>
          <StyledSegmentGroup horizontal>
            <LeftSegment>
              <embed
                  src={pdfUrl}
                  width="100%"
                  height="100%"
                  type="application/pdf"
              />
            </LeftSegment>
            <RightSegment>
              <PraxFormScrollableContainer height={"92%"}>
                <ListSegment>
                  <List divided relaxed selection>
                    {mappedPdfResponses}
                  </List>
                </ListSegment>
                {
                    showSendEmail && renderFinalForm()
                }
              </PraxFormScrollableContainer>
              <ButtonSegment>
                <Button compact
                        type="button"
                        onClick={onCloseModal}
                >
                  {getMessage("button.close")}
                </Button>
              </ButtonSegment>
            </RightSegment>
          </StyledSegmentGroup>
        </StyledModalContent>
      </StyledModal>
  );
};

export default withMessages(UnableToWorkPdfModal);
